import { FC, useEffect, useMemo, useState } from 'react';

import GapBar from './shared/GapBar';
import NumberedCallout from './shared/NumberedCallout';
import PromiseBadge from './shared/PromiseBadge';
import { capitaliseFirst, interpolateStepTemplate } from './template-utils';
import CtaButton from '@components/quiz/shared/CtaButton';
import StepHeader from '@components/quiz/shared/StepHeader';
import StickyCtaBar from '@components/quiz/shared/StickyCtaBar';
import { formatUplift } from '@components/quiz/shared/format-uplift';
import SquadPitch from '@components/manual-team-entry/squad-pitch';
import { useReadiness } from '@components/quiz/ReadinessProvider';
import { useSquadPortraits } from '@components/quiz/ReadinessProvider/utils/useSquadPortraits';
import { deriveVerdictPitchFlags } from '@components/quiz/ReadinessProvider/utils/pitch-flags';
import { fetchTransferReplacements } from '@lib/api/transfer-replacements';
import { buildSanityImageUrl } from '@lib/sanity/image';
import type { SanityReadinessVerdictBlock } from '@lib/sanity/types';

type VerdictBlockProps = SanityReadinessVerdictBlock & {
  onNext: () => void;
};

const DEFAULT_GAP_THRESHOLD = 12;

const LEAGUE_TYPE_FALLBACKS: Record<string, string> = {
  work: 'your work league',
  mates: "your mates' league",
  money: 'your money league'
};

/**
 * Step 9: the verdict (spec 6.4). Escalates then turns: gap bar, pitch
 * flags + sell note, win-odds band with its heal line, three numbered
 * callouts, the turn, the promise badge and the adaptive CTA.
 */
const VerdictBlock: FC<VerdictBlockProps> = ({
  eyebrowTemplate,
  eyebrowFallback,
  gapThreshold,
  headlineHighGap,
  headlineLowGap,
  youLabel,
  winnersLabel,
  captionTemplate,
  pitchHeading,
  sellNoteDifferTemplate,
  sellNoteAgreeTemplate,
  sellNoteNoSellTemplate,
  oddsBands,
  captainCalloutMatchTemplate,
  captainCalloutMismatchTemplate,
  captainCalloutSkippedTemplate,
  transferCalloutSelectedTemplate,
  transferCalloutRollTemplate,
  transferCalloutUndecidedTemplate,
  leagueCalloutTemplate,
  turnLine,
  promiseIcon,
  promiseHeadline,
  promiseTermsLine,
  ctaNamedLeagueTemplate,
  ctaLeagueTypeTemplate,
  ctaGenericText,
  onNext
}) => {
  const { state } = useReadiness();
  const { pitchTeam } = useSquadPortraits(state.team);
  const verdict = state.readinessVerdict;

  const userSell =
    state.transferChoice?.type === 'roll' ? null : state.selectedTransferOut;

  const pitchFlags = useMemo(
    () =>
      deriveVerdictPitchFlags({
        team: state.team,
        userCaptain: state.isCaptainSkipped ? null : state.selectedCaptain,
        userSell,
        aiCaptainId: state.aiCaptainId,
        aiSellPlayerId: state.aiSellPlayerId
      }),
    [
      state.team,
      state.isCaptainSkipped,
      state.selectedCaptain,
      userSell,
      state.aiCaptainId,
      state.aiSellPlayerId
    ]
  );

  // Rank-1 uplift (vs the out player) for the transfer callout. When the user
  // never opened the replacement cards (straight roll), the AI's top move is
  // computed for the AI's sell slot instead.
  const [fallbackRank1Points, setFallbackRank1Points] = useState<number | null>(
    null
  );

  const rank1FromState = state.transferReplacements?.[0]?.pointsUplift ?? null;

  useEffect(() => {
    if (rank1FromState !== null || !pitchFlags.aiSell) return;

    let isCancelled = false;
    const squadPlayerIds = state.team.reduce<string[]>((ids, member) => {
      if (member.player) ids.push(member.player.id);
      return ids;
    }, []);

    fetchTransferReplacements(pitchFlags.aiSell.id, squadPlayerIds)
      .then((result) => {
        if (isCancelled || !result.locked) return;
        setFallbackRank1Points(result.locked.pointsUplift);
      })
      .catch(() => undefined);

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!verdict) return null;

  const rank1Points = rank1FromState ?? fallbackRank1Points;

  const leagueDisplayName =
    state.leagueName ??
    (state.stakes ? LEAGUE_TYPE_FALLBACKS[state.stakes] : null) ??
    'your mini-league';

  const templateValues: Record<string, string | number> = {
    score: verdict.score,
    winnersTarget: verdict.winnersTarget,
    gap: verdict.gap,
    leagueName: leagueDisplayName,
    leagueType: state.stakes
      ? LEAGUE_TYPE_FALLBACKS[state.stakes]
      : 'your mini-league',
    captainName: state.selectedCaptain?.displayName ?? '',
    aiCaptainName:
      state.captainComparison?.aiCaptain.displayName ??
      pitchFlags.aiCaptain?.displayName ??
      '',
    pointsDelta:
      state.captainComparison?.pointsDelta != null
        ? Math.abs(state.captainComparison.pointsDelta).toFixed(1)
        : '…',
    rank1Points: rank1Points !== null ? formatUplift(rank1Points) : '…',
    selectedPoints:
      state.transferChoice?.type === 'replacement' &&
      state.transferReplacements?.[state.transferChoice.index]
        ? formatUplift(
            state.transferReplacements[state.transferChoice.index].pointsUplift
          )
        : '',
    selectedName:
      state.transferChoice?.type === 'replacement' &&
      state.transferReplacements?.[state.transferChoice.index]
        ? state.transferReplacements[state.transferChoice.index].player
            ?.displayName ?? ''
        : '',
    userSellName: userSell?.displayName ?? '',
    aiSellName: pitchFlags.aiSell?.displayName ?? ''
  };

  const interpolate = (template?: string) =>
    template ? interpolateStepTemplate(template, templateValues) : '';

  const eyebrow = state.leagueName
    ? interpolate(eyebrowTemplate)
    : eyebrowFallback ?? 'Your mini-league readiness';

  const headline =
    verdict.gap >= (gapThreshold ?? DEFAULT_GAP_THRESHOLD)
      ? headlineHighGap ?? 'Right now, your rivals are ahead.'
      : headlineLowGap ?? "You're in the fight. Here's the gap to close.";

  const sellNote =
    pitchFlags.sellNoteVariant === 'agree'
      ? interpolate(sellNoteAgreeTemplate)
      : pitchFlags.sellNoteVariant === 'differ'
      ? interpolate(sellNoteDifferTemplate)
      : interpolate(sellNoteNoSellTemplate);

  const oddsBand =
    verdict.oddsBandIndex >= 0 ? oddsBands?.[verdict.oddsBandIndex] : null;

  const captainCalloutText = interpolate(
    verdict.captainOutcome === 'match'
      ? captainCalloutMatchTemplate
      : verdict.captainOutcome === 'mismatch'
      ? captainCalloutMismatchTemplate
      : captainCalloutSkippedTemplate
  );

  const transferCalloutText = interpolate(
    verdict.transferOutcome === 'selected'
      ? transferCalloutSelectedTemplate
      : verdict.transferOutcome === 'roll'
      ? transferCalloutRollTemplate
      : transferCalloutUndecidedTemplate
  );

  const leagueCalloutText = capitaliseFirst(interpolate(leagueCalloutTemplate));

  const ctaText = state.leagueName
    ? interpolate(ctaNamedLeagueTemplate)
    : state.stakes
    ? interpolate(ctaLeagueTypeTemplate)
    : ctaGenericText ?? 'Unlock my plan to win my mini-league';

  return (
    <section className="flex flex-col items-center bg-neutral-100 px-4 pb-28 pt-8">
      <div className="w-full max-w-[600px]">
        {/* Verdict block */}
        <StepHeader
          eyebrow={eyebrow}
          heading={headline}
          headingDataCy="readiness-verdict-headline"
        />

        <div className="mt-6">
          <GapBar
            score={verdict.score}
            winnersTarget={verdict.winnersTarget}
            youLabel={youLabel ?? 'You'}
            winnersLabel={winnersLabel ?? 'Winners'}
            caption={interpolate(captionTemplate)}
          />
        </div>

        {/* Pitch heading */}
        {pitchHeading && (
          <p className="mb-3 mt-10 font-body text-xs font-bold uppercase tracking-widest text-neutral-500">
            {pitchHeading}
          </p>
        )}
      </div>

      {/* Pitch view — rendered outside the narrow content column so it sizes
          responsively (capped) like the captain/transfer screens and never
          crops players. */}
      <SquadPitch
        team={pitchTeam}
        flagsByPlayerId={pitchFlags.flagsByPlayerId}
      />

      <div className="w-full max-w-[600px]">
        {sellNote && (
          <p className="mt-3 font-body text-sm text-neutral-600">{sellNote}</p>
        )}

        {/* Odds block — the odds line never renders without its heal line */}
        {oddsBand && (
          <div className="mt-8 rounded-xl bg-[#F8DDE2] px-5 py-4">
            <p className="font-heading text-base font-bold text-neutral-800">
              {oddsBand.oddsLine}
            </p>
            <p className="mt-2 font-heading text-sm font-bold text-primary-500">
              {oddsBand.healLine}
            </p>
          </div>
        )}

        {/* Numbered callouts */}
        <div className="mt-8 space-y-3">
          {captainCalloutText && (
            <NumberedCallout number={1} text={captainCalloutText} />
          )}
          {transferCalloutText && (
            <NumberedCallout number={2} text={transferCalloutText} />
          )}
          {leagueCalloutText && (
            <NumberedCallout number={3} text={leagueCalloutText} />
          )}
        </div>

        {/* The turn */}
        {turnLine && (
          <p className="mt-8 text-center font-heading text-sm font-bold text-neutral-800">
            {turnLine}
          </p>
        )}

        {/* Promise badge — the only screen carrying the promise */}
        {promiseHeadline && (
          <div className="mt-6">
            <PromiseBadge
              variant="light"
              iconUrl={
                promiseIcon
                  ? buildSanityImageUrl(promiseIcon).height(96).url()
                  : undefined
              }
              headline={promiseHeadline}
              termsLine={promiseTermsLine}
            />
          </div>
        )}
      </div>

      {/* Adaptive CTA — anchored to the bottom so it stays visible on the
          long verdict page. */}
      <StickyCtaBar maxWidthClass="max-w-[600px]">
        <CtaButton onClick={onNext} dataCy="readiness-verdict-cta">
          {ctaText}
        </CtaButton>
      </StickyCtaBar>
    </section>
  );
};

export default VerdictBlock;
